import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components/macro";
import Marker from "react-leaflet-enhanced-marker";
import { empty } from "../../utils/common";

import wordmark from "../../assets/kikudoo-wordmark.png";
import theme from "../../theme.js";

/* Below this zoom the pins are the provider's logo alone. A name needs a pill wide enough to
   hold it, and at a city's worth of pins those pills collide into a wall of text. */
const LABEL_ZOOM = 14;

const Pin = styled.div`
  cursor: pointer;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  transform-origin: bottom center;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.06);
    z-index: 3;
  }
`;

const Body = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: ${props => (props.compact ? "0.25rem" : "0.25rem 0.6rem 0.25rem 0.25rem")};
  border-radius: 999px;
  background-color: ${props => (props.focus ? theme.info : theme.white)};
  color: ${props => (props.focus ? theme.white : theme.dark)};
  box-shadow: ${props =>
    props.focus ? "0 0.6rem 1.2rem rgba(0,0,0,0.35)" : "0 0.3rem 0.8rem rgba(0,0,0,0.25)"};
  border: 2px solid ${props => (props.focus ? theme.info : theme.white)};
  white-space: nowrap;
  transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
`;

const Avatar = styled.span`
  display: block;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  background-color: ${theme.gray100};
  background-image: url('${props => props.src}');
  background-size: cover;
  background-position: center;
  flex: none;
`;

const Label = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1;
`;

/* Ties the pill to its point; inherits the pill's colour so the two read as one shape. */
const Tail = styled.span`
  width: 0;
  height: 0;
  margin-top: -1px;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 7px solid ${props => (props.focus ? theme.info : theme.white)};
  filter: drop-shadow(0 2px 1px rgba(0, 0, 0, 0.18));
`;

const Count = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.15rem;
  height: 1.15rem;
  padding: 0 0.25rem;
  border-radius: 999px;
  background-color: ${props => (props.focus ? theme.white : theme.info)};
  color: ${props => (props.focus ? theme.info : theme.white)};
  font-size: 0.6875rem;
  font-weight: 700;
  line-height: 1;
`;

const truncateName = (name = "") => (name.length > 22 ? `${name.slice(0, 21)}…` : name);

/* The pin names what was found here: the first offering, or the provider when the hit is the
   provider itself. */
const labelFor = (results, provider) => {
  const [first] = results;

  return (first && first.name) || provider.name;
};

const LocationMarker = ({ location, zoom, focus, onClick }) => {
  if (!location || empty(location.providers)) {
    return "";
  }

  const results = location.results || [];
  const [provider] = location.providers;
  const showLabel = zoom >= LABEL_ZOOM || focus;

  const PinContent = () => (
    <Pin>
      <Body focus={focus} compact={!showLabel}>
        <Avatar src={provider.logo || provider.image || wordmark} />
        {showLabel && <Label>{truncateName(labelFor(results, provider))}</Label>}
        {results.length > 1 && <Count focus={focus}>{results.length}</Count>}
      </Body>
      <Tail focus={focus} />
    </Pin>
  );

  return (
    <Marker
      position={location}
      eventHandlers={{ click: () => onClick(location) }}
      icon={<PinContent />}
    />
  );
};


LocationMarker.propTypes = {
  location: PropTypes.object.isRequired,
  zoom: PropTypes.number,
  focus: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

LocationMarker.defaultProps = {
  zoom: 13,
  focus: false,
};

export default LocationMarker;
